import { Export } from '@adgate/common/interfaces/export.interface';
import { Filter } from '@adgate/common/interfaces/filter.interface';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';
import { Component, Input, OnInit, AfterViewInit, ViewChild, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService } from '@adgate/common/services/dialog-service';
import { StateService } from '@adgate/common/services/state-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { StreamingService } from '@adgate/modules/streaming/services/streaming-service';
import { CampaignFilter } from '@adgate/modules/streaming/types/campaign-filter.type';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { StreamingCampaignFiltersDialogComponent } from '@adgate/modules/streaming/dialogs/streaming-campaign-filters-dialog-component/streaming-campaign-filters-dialog-component';
import { StreamingCampaignEditDialogComponent } from '@adgate/modules/streaming/dialogs/streaming-campaign-edit-dialog-component/streaming-campaign-edit-dialog-component';
import { StreamingCampaignCreativityGridComponent } from '@adgate/modules/streaming/components/streaming-campaign-creativity-grid-component/streaming-campaign-creativity-grid-component';
import * as XLSX from 'xlsx';
import { filter, finalize, switchMap, throwError } from 'rxjs';
import { StreamingCreativityAddDialogComponent, StreamingCreativityAddDialogResult } from '@adgate/modules/streaming/dialogs/streaming-creativity-add-dialog-component/streaming-creativity-add-dialog-component';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { Creativity } from '../../types/creativity.type';
import { Status } from '@adgate/common/types/status.type';
import { SpecCheckReportDialogComponent } from '@adgate/common/components/spec-check-report-dialog-component/spec-check-report-dialog-component';
import { environment } from '@env/environment';
import { UploadDialogComponent, UploadDialogResult } from '@adgate/common/components/upload-dialog-component/upload-dialog-component';
import { StreamingMultiUploadReportDialogComponent } from '../../dialogs/streaming-multi-upload-report-dialog-component/streaming-multi-upload-report-dialog-component';

@Component({
  selector: 'streaming-campaign-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltip,
    ObjectDescPipe,
    StreamingCampaignCreativityGridComponent
  ],
  providers: [DatePipe],
  templateUrl: './streaming-campaign-grid-component.html',
  styleUrl: './streaming-campaign-grid-component.scss',
})
export class StreamingCampaignGridComponent implements OnInit, AfterViewInit, QuickSearch, Export, Filter
{
  private dialogService: DialogService = inject(DialogService);
  private streamingService: StreamingService = inject(StreamingService);
  private datePipe: DatePipe = inject(DatePipe);
  private stateService: StateService = inject(StateService);

  @Input() description: string = "Blank";

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('creativityGrid') creativityGrid?: StreamingCampaignCreativityGridComponent;

  displayedColumns: string[] = ['title', 'brand', 'agency', 'created', 'actions'];
  dataSource: MatTableDataSource<Campaign>;
  filter: CampaignFilter;
  expandedCampaign: Campaign | null = null;

  private readonly FILTER_STORAGE_KEY = 'adgate.streaming.campaign.filter';

  constructor()
  {
    this.dataSource = new MatTableDataSource<Campaign>([]);
    this.filter = new CampaignFilter();
    this.filter.status = [{ id: 20, description: 'Open' }];
  }

  ngOnInit(): void
  {
    const savedFilter = this.stateService.loadFilter(this.FILTER_STORAGE_KEY, CampaignFilter);
    if (savedFilter) {
      this.filter = savedFilter;
    }

    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getQuickSearchPlaceholder(): string
  {
    return "Search Campaigns";
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }

  onRefresh()
  {
    this.dialogService.showProgress();
    this.streamingService.getCampaigns(this.filter).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  showFilters(): boolean
  {
    return true;
  }

  onFilter()
  {
    const dialogRef = this.dialogService.openAnyDialog(StreamingCampaignFiltersDialogComponent, {
      data: this.filter,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if ( result ) {
        this.filter = result;
        this.stateService.saveFilter(this.FILTER_STORAGE_KEY, this.filter);
        this.onRefresh();
      }
    });
  }

  onExport()
  {
    if (this.dataSource.data.length > 0) {
      this.dialogService.showProgress();
      const jsonData = this.dataSource.data.map( (c:Campaign) => ({
        'Title': c.title,
        'Brand': c.brand,
        'Agency': c.agency?.description,
        'Office': c.commercialOffice?.description,
        'Created': this.datePipe.transform(c.dateCreated,'d/M/yyyy'),
        'Status': c.status.description
      }));

      let filename = "campaign-export.xlsx";
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Campaigns');
      XLSX.writeFile(wb, filename);
      this.dialogService.hideProgress();
    } else {
      this.dialogService.openErrorDialog("There are no campaigns to export");
    }
  }

  onNewCampaign(): void
  {
    this.onEditCampaign( new Campaign() );
  }

  onEditCampaign(c: Campaign)
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(StreamingCampaignEditDialogComponent, {
      data: c,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Campaign | null) => (result !== null) ),
      switchMap((result:Campaign) => {
        this.dialogService.showProgress();
        return this.streamingService.updateCampaign(c);
      }),
      switchMap((r:RESTApplicationResponse) => {
        successMessage = r.message;
        return this.streamingService.getCampaigns(this.filter);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => { 
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onAddCreativity(c: Campaign)
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(StreamingCreativityAddDialogComponent, {
      data: c,
      width: '1100px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: StreamingCreativityAddDialogResult | null) => (result !== null) ),
      switchMap((result: StreamingCreativityAddDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
          return this.streamingService.createAndUploadCreativity_HTTP(result.createCreativityRequests);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.streamingService.createAndUploadCreativity_ASPERA(result.createCreativityRequests);
        } else {
          this.dialogService.showProgress();
          return this.streamingService.createCreativity(result.createCreativityRequests);
        }
      }),
      finalize(() => {
        this.streamingService.getCampaigns(this.filter!).subscribe(r => {
          this.dataSource.data = r.data;
        });
      })
    ).subscribe({
      next: (r: RESTApplicationResponse[]) => {
        if (r.length == 1) {
          if (r[0].data != null && (r[0].data as Creativity).status.id === Status.STREAMING_CREATIVITY_SPECERROR) {
            this.onViewSpecCheckReport(r[0].data as Creativity);
          } else {
            this.dialogService.openInfoDialog(r[0].message);
          }
        } else {
          this.dialogService.openAnyDialog(StreamingMultiUploadReportDialogComponent,{
            data: r,
            width: '800px',
            minWidth: '800px',
            disableClose: true
          });
        }
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onViewSpecCheckReport(c: Creativity)
  {
    const dialogRef = this.dialogService.openAnyDialog(SpecCheckReportDialogComponent,{
      data: {
        objectType: Creativity.OBJECT_TYPE,
        objectId: c.id,
        title: `${c.referenceCode}`,
        allowUpload: true,
        specsUrl: `${environment.helpCenterUrl}#specs-streaming`
      },
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result) => result != null)
    ).subscribe((result:'upload' | 'view-specs') => {
      if( result === 'upload' ) {
        this.onUploadCreativity(c);
      }
    });
  }

  onUploadCreativity(c: Creativity): void
  {
    const dialogRef = this.dialogService.openAnyDialog(UploadDialogComponent, {
      width: '600px',
      minWidth: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result:UploadDialogResult | null) => result !== null),
      switchMap((result:UploadDialogResult) => {
        if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
        return this.streamingService.uploadCreativity_HTTP(c.id, result.media);
        } else if( result.uploadMethod.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
          this.dialogService.showProgress();
          return this.streamingService.uploadCreativity_ASPERA(c.id, result.media);
        } else {
          return throwError(() => new Error('Invalid upload method'));
        }
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }
  
  private isCampaignExpanded(c: Campaign): boolean
  {
    return this.expandedCampaign?.id === c.id;
  }

  onExpandCampaign(c: Campaign)
  {
    this.expandedCampaign = this.isCampaignExpanded(c) ? null : c;
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'title': return item.title?.toLowerCase() || '';
        case 'brand': return item.brand?.toLowerCase() || '';
        case 'agency': return item.agency?.description?.toLowerCase() || '';
        case 'office': return item.commercialOffice?.description?.toLowerCase() || '';
        case 'created': return item.dateCreated;
        default: return item[property];
      }
    };
  }

}
